import { useEffect, useState } from "react";
import { api, assetUrl } from "../lib/api";
import { useBrand } from "../context/BrandContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ChevronLeft, ChevronRight } from "lucide-react";

const STATUS_DOT = { pending_review: "bg-muted-foreground", approved: "bg-accent", changes_requested: "bg-destructive", scheduled: "bg-primary", published: "bg-primary/70" };
const PLATFORM_LABEL = { instagram: "IG", linkedin: "LI", twitter: "X", facebook: "FB", tiktok: "TT" };

export default function CalendarPage() {
  const { selected } = useBrand();
  const [posts, setPosts] = useState([]);
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => { api.get("/posts", { params: selected ? { brand: selected } : {} }).then((r) => setPosts(r.data)); }, [selected]);

  const firstDay = (month.getDay() + 6) % 7;
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const postsOn = (day) => posts.filter((p) => {
    const d = new Date(p.scheduled_at);
    return d.getFullYear() === month.getFullYear() && d.getMonth() === month.getMonth() && d.getDate() === day;
  });

  return (
    <div className="space-y-6 fade-up" data-testid="calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-1">Content Calendar{selected ? ` · ${selected}` : ""}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold">{month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" data-testid="prev-month-btn" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}><ChevronLeft size={16} /></Button>
          <Button variant="outline" size="icon" data-testid="next-month-btn" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}><ChevronRight size={16} /></Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-px bg-border rounded-xl overflow-hidden border border-border">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="bg-card px-2 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-center">{d}</div>
        ))}
        {cells.map((day, i) => (
          <div key={i} className="bg-card min-h-[110px] p-2 space-y-1">
            {day && <p className="text-xs font-semibold text-muted-foreground">{day}</p>}
            {day && postsOn(day).map((p) => (
              <button key={p.id} data-testid={`cal-post-${p.id}`} onClick={() => setSelectedPost(p)}
                className={`w-full text-left flex items-center gap-1.5 px-1.5 py-1 rounded bg-muted hover:bg-secondary transition-colors ${p.video_url ? "border-l-2 border-accent-2" : ""}`}>
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_DOT[p.review_status]}`} />
                <span className="text-[10px] truncate">{PLATFORM_LABEL[p.platform]} · {p.title}</span>
              </button>
            ))}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        {Object.entries({ pending_review: "Pending", approved: "Approved", changes_requested: "Changes requested", scheduled: "Scheduled", published: "Published" }).map(([k, l]) => (
          <span key={k} className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${STATUS_DOT[k]}`} /> {l}</span>
        ))}
        <span className="flex items-center gap-1.5"><span className="w-2 h-3 rounded-sm border-l-2 border-accent-2 bg-muted" /> Video post</span>
      </div>

      <Dialog open={!!selectedPost} onOpenChange={(v) => !v && setSelectedPost(null)}>
        <DialogContent data-testid="calendar-post-dialog">
          {selectedPost && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{selectedPost.title}</DialogTitle></DialogHeader>
              {selectedPost.video_url ? (
                <video src={assetUrl(selectedPost.video_url)} poster={selectedPost.video_poster_url ? assetUrl(selectedPost.video_poster_url) : (selectedPost.video_image_url ? assetUrl(selectedPost.video_image_url) : (selectedPost.image_url ? assetUrl(selectedPost.image_url) : undefined))} controls playsInline className="w-full rounded-lg max-h-64 bg-black" data-testid="calendar-post-video" />
              ) : selectedPost.image_url && <img src={assetUrl(selectedPost.image_url)} alt="" className="w-full rounded-lg max-h-64 object-cover" />}
              <div className="flex gap-2 flex-wrap">
                <Badge variant="secondary" className="capitalize">{selectedPost.platform}</Badge>
                <Badge variant="secondary" className="capitalize">{selectedPost.pillar}</Badge>
                {selectedPost.brand && <Badge className="bg-accent text-accent-foreground border-0">{selectedPost.brand}</Badge>}
                {selectedPost.video_url && <Badge className="bg-accent-2 text-accent-2-foreground border-0">Video</Badge>}
                <Badge className="capitalize">{selectedPost.review_status.replace("_", " ")}</Badge>
              </div>
              <p className="text-sm whitespace-pre-wrap">{selectedPost.copy}</p>
              <p className="text-xs text-accent">{(selectedPost.hashtags || []).map((h) => `#${h}`).join(" ")}</p>
              <p className="text-xs text-muted-foreground">Scheduled: {new Date(selectedPost.scheduled_at).toLocaleString()}</p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
